/*---------------------------------------------------------------------------------------------
 * Copyright (c) Bentley Systems, Incorporated. All rights reserved.
 * See LICENSE.md in the project root for license terms and full copyright notice.
 *--------------------------------------------------------------------------------------------*/
import { IModelConnection, ScreenViewport } from "@itwin/core-frontend";
import { SpatialClassifier, SpatialClassifierFlags } from "@itwin/core-common";
import { SpatialClassifierInsideDisplay, SpatialClassifierOutsideDisplay } from "@itwin/core-common";

import { SpatialViewState, Viewport } from "@itwin/core-frontend";

import { cRealityData } from "./RealityData";
import { cView } from "./View";
import { cQuery } from "./Query";

export class cClassifier {
  private static iModel: IModelConnection;
  private static sViewPort: ScreenViewport;
  private static classifiers: Map<string, string>;

  public static async initClassifier(vp: ScreenViewport, imodel: IModelConnection) {
    this.iModel = imodel;
    this.sViewPort = vp;

    this.classifiers = new Map();
    await this.getAvailableClassifierListForViewport(this.sViewPort);
    this.addClassifiersToRealityData();
    this.setupClassifier("$Index");
  }

  /**
   * Query the iModel to get available spatial classifiers.
   **/

  public static async getAvailableClassifierListForViewport(vp?: Viewport) {
    if (!vp || !(vp.view instanceof SpatialViewState)) return;

    // get classifiers models
    const curModelProps = await cQuery.fetchModelProps("$Classifier");
    for (const modelProps of curModelProps) {
      if (modelProps.name) {
        const model = modelProps.name.substring(0, modelProps.name.indexOf(",")         );
        this.classifiers.set(model, modelProps.id!);
      }
    }
  }

  public static async addClassifiersToRealityData() {
    for (const [name, modelId] of this.classifiers) {
      const classifier: SpatialClassifier = this.getClassifierValues(name, modelId);
      this.updateRealityDataClassifiers(classifier);
    }
  }

  // Update the classifier in the ViewPort
  public static updateRealityDataClassifiers(classifier: SpatialClassifier) {
    const realityModels = cRealityData.getActiveRealityModels();
    for (const realityModel of realityModels.models) {
      // Loop through all classifiers in the reality model.
      if (realityModel && realityModel.classifiers) {
        let existingClassifier: SpatialClassifier | undefined;
        for (const c of realityModel.classifiers) {
          if (c.name === classifier.name) {
            existingClassifier = c;
          }
        }

        // If the classifier doesn't exist, add it to reality model classifiers
        // If the classifier exists, replace it with new classifier properties
        if (!existingClassifier) 
          realityModel.classifiers.add(classifier);
        else 
          realityModel.classifiers.replace(existingClassifier, classifier);
      }
    }
  }

  // Get property values for the classifier
  private static getClassifierValues(name: string, modelId: string): SpatialClassifier {
    let inside: SpatialClassifierInsideDisplay;
    if (name === "$Index") {
      inside = SpatialClassifierInsideDisplay.ElementColor;
    } else {
      inside = SpatialClassifierInsideDisplay.On;
    }

    const outside = SpatialClassifierOutsideDisplay.On;
    const isVolume = true;
    const flags = new SpatialClassifierFlags(inside, outside, isVolume);

    const expandDist = 0.1;
//    const classifier= new SpatialClassifier(modelId, `${modelId}`, flags, expandDist);
    const classifier = new SpatialClassifier(modelId, name, flags, expandDist);

    return classifier;
  }

  // Setup the classifier in the ViewPort
  public static setupClassifier(classifierName: string) {

    const realityModels = cRealityData.getActiveRealityModels();
    for (const realityModel of realityModels.models) {
      if (realityModel && realityModel.classifiers) {
        for (const c of realityModel.classifiers) {
          if (c.name === classifierName) {
            realityModel.classifiers.setActive(c);
            this.sViewPort.invalidateScene();
          }
        }
      }
    }
  }

  public static removeClassifiers() {
    const realityModels = cRealityData.getActiveRealityModels();
    for (const realityModel of realityModels.models) {
      if (realityModel && realityModel.classifiers) {
        realityModel.classifiers.setActive(undefined);
        this.sViewPort.invalidateScene();
      }
    }
  }
}
